import type { PreLoadedDoc } from '@/lib/templates/types';

function normalizeEvidenceText(value: string | null | undefined): string {
  return (value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function includesAny(haystack: string, needles: string[]): boolean {
  return needles.some((needle) => haystack.includes(normalizeEvidenceText(needle)));
}

function documentEvidenceText(doc: Pick<PreLoadedDoc, 'type' | 'extracted_text'>): string {
  return normalizeEvidenceText(`${doc.type || ''} ${doc.extracted_text || ''}`);
}

export function attachmentMatchesDocument(
  attachment: string,
  doc: Pick<PreLoadedDoc, 'type' | 'extracted_text'>,
): boolean {
  const requirement = normalizeEvidenceText(attachment);
  const docType = normalizeEvidenceText(doc.type);
  const evidence = documentEvidenceText(doc);

  if (!requirement || !docType) return false;
  if (docType.includes(requirement) || requirement.includes(docType)) return true;

  if (includesAny(requirement, ['clinical packet', 'supporting document'])) {
    return evidence.length > 0;
  }

  if (includesAny(requirement, ['mri', 'magnetic resonance'])) {
    return includesAny(evidence, ['mri', 'magnetic resonance']);
  }

  if (includesAny(requirement, ['x ray', 'xray', 'radiograph', 'weight bearing', 'standing films'])) {
    return includesAny(evidence, ['x ray', 'xray', 'radiograph', 'weight bearing', 'standing ap', 'kellgren']);
  }

  if (includesAny(requirement, ['imaging', 'diagnostic report', 'diagnostic study'])) {
    return includesAny(evidence, [
      'mri',
      'magnetic resonance',
      'x ray',
      'xray',
      'radiograph',
      'ct',
      'emg',
      'ncs',
      'nerve conduction',
      'ultrasound',
    ]);
  }

  if (includesAny(requirement, ['physical therapy', 'pt ', 'therapy notes', 'conservative care', 'nsaid history'])) {
    return includesAny(evidence, [
      'physical therapy',
      'pt summary',
      'supervised therapy',
      'conservative care',
      'home exercise',
      'nsaid',
      'activity modification',
    ]);
  }

  if (includesAny(requirement, ['injection'])) {
    return includesAny(evidence, ['injection', 'corticosteroid', 'viscosupplement', 'subacromial']);
  }

  if (includesAny(requirement, ['emg', 'ncs', 'nerve conduction'])) {
    return includesAny(evidence, ['emg', 'ncs', 'nerve conduction', 'median neuropathy']);
  }

  if (includesAny(requirement, ['clinical notes', 'clinical note', 'orthopedic evaluation', 'evaluation', 'h and p', 'history and physical'])) {
    return includesAny(evidence, [
      'orthopedic evaluation',
      'evaluation',
      'clinical exam',
      'follow up',
      'history',
      'physical exam',
      'surgeon',
    ]);
  }

  if (includesAny(requirement, ['medical clearance', 'clearance'])) {
    return includesAny(evidence, ['medical clearance', 'no acute contraindication', 'perioperative risk']);
  }

  if (includesAny(requirement, ['hba1c', 'hemoglobin a1c', 'glycemic'])) {
    return includesAny(evidence, ['hba1c', 'hemoglobin a1c', 'glycemic']);
  }

  if (includesAny(requirement, ['cotinine', 'nicotine'])) {
    return includesAny(evidence, ['cotinine', 'nicotine']);
  }

  if (includesAny(requirement, ['cardiology', 'cardiac', 'cad'])) {
    return includesAny(evidence, ['cardiology', 'cardiac', 'coronary artery disease', 'cad']);
  }

  if (includesAny(requirement, ['prior tka', 'index tka', 'prior arthroplasty', 'index arthroplasty'])) {
    return includesAny(evidence, ['prior tka', 'index tka', 'total knee arthroplasty', 'arthroplasty performed']);
  }

  if (includesAny(requirement, ['signed physician order', 'physician order', 'surgical order'])) {
    return includesAny(evidence, ['recommended', 'ordered', 'surgeon', 'medically necessary']);
  }

  return false;
}

export function hasMatchingAttachment(
  attachment: string,
  docs: Array<Pick<PreLoadedDoc, 'type' | 'extracted_text'>>,
): boolean {
  return docs.some((doc) => attachmentMatchesDocument(attachment, doc));
}

export function findMatchingAttachmentDoc(
  attachment: string,
  docs: Array<Pick<PreLoadedDoc, 'type' | 'extracted_text'>>,
): Pick<PreLoadedDoc, 'type' | 'extracted_text'> | null {
  return docs.find((doc) => attachmentMatchesDocument(attachment, doc)) || null;
}
